/**
 * ONE TAP — the sun. (Art direction: SUNLIT CEL, DESIGN.md 4.)
 *
 * THE PREMISE CHANGED. This rig used to light an enclosed multi-deck interior, and every
 * number in it was an apology for that: a cascade wide enough to see a roof sixty metres
 * overhead, a bounce pole strong enough to keep soffits from reading as holes, a shadow
 * boundary pushed past the arena's sightlines because "fully lit" is a lie indoors.
 *
 * The arena is now a ROOFTOP COMPLEX UNDER OPEN SKY (DESIGN.md 4, "The Arena Is a ROOFTOP").
 * Outdoors, all three of those apologies invert:
 *
 *  - THE SUN LANDS ON THE PLAY SPACE. Almost every surface a player stands on is sun-facing
 *    or sun-facing-and-shadowed-by-something. `dot(N, sunDir)` is a real signal again, so the
 *    key can be the loudest thing in the rig and the shadow it throws is the frame's subject
 *    rather than a special case.
 *  - THE SKY IS THE FILL, AND IT IS ENORMOUS. A rooftop deck sees an unobstructed hemisphere.
 *    The sky pole is therefore a genuine light with a genuine hue — cool, blue, and worth a
 *    third of a sun — and it is what keeps the shadow side FULL OF LIGHT while the sunlit side
 *    gets genuinely hot. That difference is the whole "high key" instruction.
 *  - BEYOND THE CASCADE, "FULLY LIT" IS THE TRUTH. Three reports every fragment outside the
 *    shadow camera's ortho frustum as unshadowed. Indoors that was catastrophic and forced a
 *    240m-wide cascade with garbage texels. On a roof it is simply correct for the far half of
 *    the city, so the cascade shrinks, the texel halves, and the shadow terminator gets crisp.
 *
 * Division of labour is unchanged and still the point: THIS FILE owns where the sun is and how
 * big a volume gets real cast shadows. TOON.TS owns what value a surface lands on, reading the
 * irradiance figures published at the bottom of this file so the two can never disagree.
 */

import * as THREE from 'three';
import { PALETTE, SHADOW_MAP_SIZE } from '@shared/constants';

/**
 * SUN ANGLE. Outdoors this is the single most consequential number in the renderer: it decides
 * how long every shadow in the game is.
 *
 * 34 degrees of elevation throws a shadow 1 / tan(34) = 1.48x the height of whatever threw it.
 * A 1.1m parapet lays a 1.6m band across the deck behind it; a 3m plant-room box lays four and
 * a half metres. That is "long dramatic shadows across open decks" as an actual measurement,
 * and it is what turns ducting, vents and handrails into readable graphic bands instead of
 * smudges under their own footprint. Lower still would be prettier and would start hiding
 * whole decks in shade, which is a readability cost this game cannot pay.
 *
 * The azimuth is deliberately NOT on the 45-degree diagonal. On the diagonal both wall axes
 * take identical irradiance and the corner between them disappears. At 22 degrees off the X
 * axis the four orientations separate cleanly through the toon ramp, with wide margins either
 * side of toon.ts's BAND_HIGHLIGHT (0.42):
 *
 *     +X wall  dot = cos34 cos22 = 0.77  -> lit band
 *     deck     dot = sin34        = 0.56 -> lit band
 *     +Z wall  dot = cos34 sin22 = 0.31  -> mid band
 *     -X / -Z                     < 0    -> shadow band
 *
 * Three bands from geometry alone, before a single cast shadow is drawn.
 */
const SUN_ELEVATION_DEG = 34;
/**
 * Where the sun sits at HIGH NOON (DESIGN.md 2). The name is ironic on purpose — sudden death
 * drops the sun toward the horizon rather than raising it, because the brief for that state is
 * "escalate without recolouring the world", and a lower sun is the one lever that lengthens and
 * deepens every shadow in the arena while leaving the palette exactly where it was. Shadows
 * stretch from 1.48x to 1.88x the height of their caster over the ramp.
 *
 * IT DOES NOT GO LOWER THAN THIS, and the reason is the toon ramp, not taste. A deck's dotNL is
 * sin(elevation); at 28 degrees that is 0.469, comfortably above BAND_HIGHLIGHT. Drop the sun
 * much further and every open deck in the arena steps DOWN into the mid band at once, which
 * darkens the sunlit half of the ground toward the shadowed half — i.e. sudden death would
 * REDUCE shadow contrast, which is the exact opposite of the brief. The other three
 * orientations have margin to spare at this angle (+Z wall 0.33, still mid; +X wall 0.82,
 * still lit), so 28 degrees is where the ramp says to stop.
 */
const SUN_ELEVATION_NOON_DEG = 28;
const SUN_AZIMUTH_DEG = 22;

/**
 * Half-size of the shadow cascade, in metres, and how far ahead of the camera it sits.
 *
 * THIS IS HALF WHAT IT WAS, and the reason is the pivot. Outside the ortho frustum three
 * reports every fragment as fully lit. In the old enclosed arena that was wrong for nearly
 * every surface, so the boundary had to be shoved past the map's longest sightline (120m) and
 * the texel paid for it. Outdoors "unshadowed" is the correct answer for the distant city and
 * for anything past the far parapet, so the cascade only has to cover the decks a player can
 * actually read shadow SHAPES on — and the last stretch of it is faded out in the shader
 * anyway (see SHADOW_FADE_*).
 *
 * Guaranteed shadow radius is EXTENT - LEAD = 74m: a point that far from the camera is inside
 * the frustum no matter which way the sun's basis falls. The lead is bigger than it was
 * because outdoors the interesting shadows are the long ones running away from you down a
 * deck, i.e. in front.
 *
 * Texel size, for the record: 176m / 4096 = 43mm on high, 86mm on medium, 172mm on low. The
 * old rig managed 59mm only at 4096 and 234mm at the bottom tier.
 */
const SHADOW_EXTENT = 88;
const SHADOW_LEAD = 14;
/** Cast shadows fade out over this range so the cascade boundary is never a visible edge. */
const SHADOW_FADE_END = SHADOW_EXTENT - SHADOW_LEAD;
const SHADOW_FADE_START = SHADOW_FADE_END * 0.72;
/**
 * Depth range of the ortho shadow camera, in metres along the sun ray. Half of it sits in
 * front of the cascade centre, so this is "how far up-sun an occluder can be and still cast".
 *
 * A rooftop complex stacks its decks vertically, and a tower 70m above the deck you are
 * standing on is 70 / sin(28 deg) = 149m away along the low-sun ray. 240m clears that with
 * margin at the shallowest angle HIGH NOON can reach, which is the case that matters: the
 * whole point of dropping the sun is that things far up-sun start shading things far down-sun.
 */
const SHADOW_DEPTH = 240;
/**
 * How much light a cast shadow removes. Not 1.0: a shadowed rooftop still sees the entire sky
 * hemisphere, and that is precisely what keeps a shadow on a white deck a cool blue-grey
 * rather than a hole (DESIGN.md 4 — never crush to black).
 *
 * High, though, because the difference between "in the sun" and "behind the plant room" has to
 * be the strongest single lighting event in the frame — it is the only one with a SHAPE.
 */
export const SHADOW_INTENSITY = 0.9;
/**
 * ...and at HIGH NOON. Deeper, so the lengthening shadows also darken. toon.ts bakes the base
 * figure into its normalisation; raising it at runtime can only push a shadowed surface further
 * toward the bottom of its own band, never out of it, so the value ladder survives sudden death.
 */
const SHADOW_INTENSITY_NOON = 0.96;
/**
 * PCF softening radius, in shadow texels. DESIGN.md 4 asks for a crisp terminator, so this is
 * small — with a 4096 map over a 176m-wide cascade one texel is 43mm, and 1.2 of them is a 5cm
 * penumbra. Enough to antialias the edge, nowhere near enough to blur it.
 */
const SHADOW_RADIUS = 1.2;
/**
 * `normalBias` in SHADOW TEXELS rather than in metres. Acne is a texel-space problem — one
 * shadow texel covers a fixed slab of world and the offset has to clear it — so the metric
 * value has to be recomputed whenever the cascade size or the map size changes. Keeping it in
 * texels is what stops the lower quality tiers (half the map, double the texel) from acneing.
 */
const SHADOW_NORMAL_BIAS_TEXELS = 1.3;

/** World size of one shadow texel for a given map resolution. */
function shadowTexelSize(mapSize: number): number {
  return (SHADOW_EXTENT * 2) / Math.max(1, mapSize);
}

/**
 * KEY COLOUR — and it is deliberately NOT `PALETTE.sun` any more.
 *
 * Playtest: "it could feel a little more sunny. Like it's a beautiful day, insanely pristine."
 * The rig was measurably correct daylight and emotionally flat, and the single cheapest fix for
 * that is HUE SEPARATION between the two light sources. `PALETTE.sun` (#FFF6E0) is the colour of
 * the sun DISC — nearly white, because a disc that reads as cream reads as sunset. The light it
 * throws should be warmer than the object it comes from, so the key gets its own tone: about a
 * 300K drop, taking the blue channel from 0.88 to 0.82 of the red.
 *
 * The value ladder cannot notice this. toon.ts normalises incident colour to luminance 1 before
 * it tints anything (LIGHT_HUE), so hue moves and brightness does not; and this file's published
 * KEY_IRRADIANCE re-derives from the colour automatically, so the ladder re-calibrates itself.
 * What DOES move is the warm/cool split, which is the entire feeling of a sunny day. Measured
 * end-to-end through toon.ts and the whole composite: a sunlit white wall renders #FDF2DC — 34
 * sRGB codes of red-over-blue, was 27 — and a deck in a cast shadow #6E8097, 41 codes of
 * blue-over-red, was 31. Fifty-one codes of hue swing across a terminator that is only fifteen
 * L* deep, and it costs no contrast at all.
 *
 * IT STOPS HERE, and the reason is a channel, not taste. The brief asks for sunlit white to sit
 * JUST UNDER clipping; at this colour the brightest surface the arena can make (a white wall
 * square-on to the sun, top of its band) lands on sRGB #FDF2DC — red at 253 of 255. Any warmer
 * and the red channel clips before the value ladder's top rung is reached, which flattens the
 * terminator on exactly the surface the whole art direction is about.
 */
const KEY_COLOR = 0xfff2d0;
/**
 * The hemisphere's two poles. Outdoors these stop being a fudge and become the actual physics
 * of a rooftop: everything up-facing sees sky, everything down-facing sees the deck below it.
 *
 * SKY IS A REAL BLUE AND IT IS BIGGER THAN IT WAS. An enclosed room only ever saw the sky
 * through a hole, so a saturated fill painted every upward face steel and read as one cold wash;
 * a roof deck sees the whole hemisphere. It is pushed further from white again (0xbedfff ->
 * 0xa9d6ff -> 0x9ccdff) for the same reason the key got warmer: shadow on a rooftop is SKY-LIT,
 * and a shadow that reads as cool luminous blue is a completely different emotion from one that
 * reads as merely dark. Measured across those three values, a cast shadow on a white deck goes
 * #7b8592 -> #748396 -> #6E8097, i.e. from 23 to 31 to 41 sRGB codes of blue-over-red.
 *
 * "LUMINOUS" IS CARRIED BY CHROMA, NOT BY L*, and that is the whole reason this is safe to push.
 * The shadowed deck moved DOWN 1.5 L* over this pass (54.3 -> 52.8) while its HSV chroma went
 * 21% -> 27%: the shadow got deeper AND bluer at once. A cool saturated shadow at L* 53 reads as
 * sky-light bouncing into it; a neutral grey one at L* 55 reads as dirt. This is also what pays
 * for the sun-vs-shadow figure going UP (14.3 -> 15.5) in a pass whose brief was "brighter".
 *
 * GROUND is the bounce off pale concrete under a hot sun: warm, and warmer and brighter than
 * the muddy terrazzo tone an interior needed, because white concrete in full sun is a genuinely
 * strong secondary source. It is the only light a soffit, a canopy underside or the belly of a
 * walkway ever gets, so it decides what colour "dark" is in this game.
 */
const HEMI_SKY = 0x9ccdff;
const HEMI_GROUND = 0xf2e2c8;

/**
 * INTENSITIES. Three divides diffuse by PI (BRDF_Lambert), so an intensity of PI lands exactly
 * 1.0 of irradiance on a fully-lit surface. Writing them this way makes the numbers below read
 * as what they are, as a fraction of "one full sun".
 *
 * HIGH KEY, AND A BIG COOL FILL UNDER IT. The key is up (1.35 -> 1.45 suns) and the sky
 * hemisphere with it (0.34 -> 0.40 -> 0.43). They move together on purpose: raising the key
 * alone deepens every shadow, and a deep shadow in a high-key world is the failure DESIGN.md 4
 * forbids. Raising the sky alongside it keeps the shadow side full of cool light while the
 * sunlit side gets genuinely hot, which is exactly the difference between "bright" and
 * "washed out".
 *
 * WHAT THE STEPS ACTUALLY BOUGHT, measured end-to-end through toon.ts's bands and the whole
 * postfx grade: the sun-vs-shadow difference on one slab of deck went 13.7 -> 14.3 -> 15.5 L*,
 * and at HIGH NOON 14.6 -> 15.2 -> 16.7. i.e. the frame got BRIGHTER AND MORE CONTRASTY at once,
 * which is the only version of "sunnier" worth having — the original relight failed precisely
 * because brighter meant flatter. The worst-case gaps in the value ladder are 18.9 and 16.6
 * against a bar of 15 (19.0 and 18.7 at HIGH NOON), because the bands are authored in L* and the
 * ramp re-normalises to whatever this file publishes; see toon.ts SHADE_MIN / SHADE_MAX.
 *
 * The ambient exists for one reason only — to guarantee that the darkest pixel in the game is
 * a lifted cool grey and never black. On a roof there is very little for it to do, so it is
 * smaller than it was indoors.
 */
const KEY_INTENSITY = Math.PI * 1.45;
const HEMI_INTENSITY = Math.PI * 0.43;
const AMBIENT_INTENSITY = Math.PI * 0.05;

const LIGHT_DISTANCE = 70;

/** Relative luminance of a palette colour, in linear light. */
function linearLuminance(hex: number): number {
  const c = new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
}

/**
 * IRRADIANCE FIGURES, published so the toon grade can normalise the light it receives into a
 * 0..1 "how sunlit is this" factor without hard-coding anything about this rig. Each is the
 * luminance a white surface receives from that source, already divided by PI.
 *
 * These are the contract between lighting.ts and toon.ts. Retune an intensity here and the
 * value ladder over there re-derives itself.
 */
export const KEY_IRRADIANCE = (KEY_INTENSITY / Math.PI) * linearLuminance(KEY_COLOR);
export const HEMI_IRRADIANCE_SKY = (HEMI_INTENSITY / Math.PI) * linearLuminance(HEMI_SKY);
export const HEMI_IRRADIANCE_GROUND = (HEMI_INTENSITY / Math.PI) * linearLuminance(HEMI_GROUND);
export const AMBIENT_IRRADIANCE = (AMBIENT_INTENSITY / Math.PI) * linearLuminance(PALETTE.shadow);

export interface LightingRig {
  readonly key: THREE.DirectionalLight;
  readonly hemi: THREE.HemisphereLight;
  readonly ambient: THREE.AmbientLight;
  /**
   * Unit vector pointing from the world toward the key light. Shared with the sky and the toon
   * grade. MUTATED IN PLACE by {@link LightingRig.setHighNoon} — anything that caches a copy of
   * it (the sky's uniform) has to be re-pushed each frame, which the renderer does.
   */
  readonly sunDirection: THREE.Vector3;
  /**
   * View distances (x = start, y = end) over which cast shadows fade to nothing, so the
   * cascade's frustum boundary is never drawn as a hard edge. The toon grade reads this.
   */
  readonly shadowFade: THREE.Vector2;
  /**
   * HIGH NOON, 0..1. Drops the sun toward the horizon and deepens the cast shadow. Deliberately
   * changes no colour and no intensity: sudden death escalates the LIGHT, never the palette.
   */
  setHighNoon(t: number): void;
  /** Re-fit and texel-snap the shadow cascade around the camera. Call once per frame. */
  update(camera: THREE.Camera): void;
  setShadows(enabled: boolean, mapSize: number): void;
  dispose(): void;
}

const _center = new THREE.Vector3();
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _worldUp = new THREE.Vector3(0, 1, 0);

const DEG = Math.PI / 180;

export function createLighting(scene: THREE.Scene): LightingRig {
  const azimuth = SUN_AZIMUTH_DEG * DEG;
  const sunDirection = new THREE.Vector3();
  const shadowFade = new THREE.Vector2(SHADOW_FADE_START, SHADOW_FADE_END);

  function aimSun(elevationDeg: number): void {
    const e = elevationDeg * DEG;
    sunDirection
      .set(Math.cos(e) * Math.cos(azimuth), Math.sin(e), Math.cos(e) * Math.sin(azimuth))
      .normalize();
  }
  aimSun(SUN_ELEVATION_DEG);

  const key = new THREE.DirectionalLight(
    new THREE.Color().setHex(KEY_COLOR, THREE.SRGBColorSpace),
    KEY_INTENSITY,
  );
  key.name = 'KeyLight';
  key.castShadow = true;
  key.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
  key.shadow.camera.near = 1;
  key.shadow.camera.far = SHADOW_DEPTH;
  key.shadow.camera.left = -SHADOW_EXTENT;
  key.shadow.camera.right = SHADOW_EXTENT;
  key.shadow.camera.top = SHADOW_EXTENT;
  key.shadow.camera.bottom = -SHADOW_EXTENT;
  // normalBias offsets the sample along the surface normal, which kills acne on every slope
  // angle at once. The tiny constant bias only cleans up grazing self-shadowing.
  key.shadow.bias = -0.0003;
  key.shadow.normalBias = shadowTexelSize(SHADOW_MAP_SIZE) * SHADOW_NORMAL_BIAS_TEXELS;
  key.shadow.radius = SHADOW_RADIUS;
  key.shadow.intensity = SHADOW_INTENSITY;
  key.shadow.camera.updateProjectionMatrix();
  scene.add(key);
  scene.add(key.target);

  const hemi = new THREE.HemisphereLight(
    new THREE.Color().setHex(HEMI_SKY, THREE.SRGBColorSpace),
    new THREE.Color().setHex(HEMI_GROUND, THREE.SRGBColorSpace),
    HEMI_INTENSITY,
  );
  hemi.name = 'HemiFill';
  scene.add(hemi);

  const ambient = new THREE.AmbientLight(
    new THREE.Color().setHex(PALETTE.shadow, THREE.SRGBColorSpace),
    AMBIENT_INTENSITY,
  );
  ambient.name = 'Ambient';
  scene.add(ambient);

  let shadowsEnabled = true;
  let mapSize = SHADOW_MAP_SIZE;
  let noon = 0;

  function setHighNoon(t: number): void {
    const k = t < 0 ? 0 : t > 1 ? 1 : t;
    if (k === noon) return;
    noon = k;
    aimSun(SUN_ELEVATION_DEG + (SUN_ELEVATION_NOON_DEG - SUN_ELEVATION_DEG) * k);
    key.shadow.intensity = SHADOW_INTENSITY + (SHADOW_INTENSITY_NOON - SHADOW_INTENSITY) * k;
  }

  function update(camera: THREE.Camera): void {
    if (!shadowsEnabled) return;

    // Cascade centre: a little ahead of the camera along its flattened view direction.
    camera.getWorldDirection(_forward);
    _forward.y = 0;
    if (_forward.lengthSq() < 1e-6) _forward.set(0, 0, -1);
    else _forward.normalize();

    _center.setFromMatrixPosition(camera.matrixWorld).addScaledVector(_forward, SHADOW_LEAD);

    // Build an orthonormal basis on the shadow plane and quantise the centre to the texel grid
    // in that basis. Without this the ortho frustum slides continuously and every shadow edge
    // boils — which on long low-sun shadows is far more visible than it was on short ones.
    _right.copy(_worldUp).cross(sunDirection);
    if (_right.lengthSq() < 1e-6) _right.set(1, 0, 0);
    else _right.normalize();
    _up.copy(sunDirection).cross(_right).normalize();

    const texel = shadowTexelSize(mapSize);
    const rx = Math.round(_center.dot(_right) / texel) * texel;
    const ry = Math.round(_center.dot(_up) / texel) * texel;
    const rz = _center.dot(sunDirection);

    _center
      .set(0, 0, 0)
      .addScaledVector(_right, rx)
      .addScaledVector(_up, ry)
      .addScaledVector(sunDirection, rz);

    key.target.position.copy(_center);
    key.position.copy(_center).addScaledVector(sunDirection, SHADOW_DEPTH * 0.5);
    key.target.updateMatrixWorld();
    key.updateMatrixWorld();
  }

  function setShadows(enabled: boolean, size: number): void {
    shadowsEnabled = enabled;
    key.castShadow = enabled;
    if (size !== mapSize) {
      mapSize = size;
      key.shadow.mapSize.set(size, size);
      // A smaller map means bigger texels, which means a bigger offset is needed to clear
      // them. Keeping the bias in texels is what stops the lower quality tiers acneing.
      key.shadow.normalBias = shadowTexelSize(size) * SHADOW_NORMAL_BIAS_TEXELS;
      if (key.shadow.map !== null) {
        key.shadow.map.dispose();
        key.shadow.map = null;
      }
    }
  }

  function dispose(): void {
    scene.remove(key, key.target, hemi, ambient);
    key.dispose();
    hemi.dispose();
    ambient.dispose();
    if (key.shadow.map !== null) {
      key.shadow.map.dispose();
      key.shadow.map = null;
    }
  }

  // The key is the only light with a position that matters (the shadow camera hangs off it);
  // it is placed properly on the first update(). Seed it so frame zero is not degenerate.
  key.position.copy(sunDirection).multiplyScalar(LIGHT_DISTANCE);
  key.updateMatrixWorld();

  return { key, hemi, ambient, sunDirection, shadowFade, setHighNoon, update, setShadows, dispose };
}
